import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import {
  ManagedServiceType,
  ServiceProvider,
  ServiceStatus,
  MANAGED_SERVICE_LABEL,
} from '@masamune/shared';

interface ClientLite { id: string; legalName: string; }

const PLAN_PRESETS: Record<keyof typeof ManagedServiceType, Array<{ code: string; label: string; amount: number; cost: number }>> = {
  HOSTING: [
    { code: 'HOSTING_S', label: 'Hosting Start',   amount: 29,  cost: 6 },
    { code: 'HOSTING_M', label: 'Hosting Pro',     amount: 59,  cost: 12 },
    { code: 'HOSTING_L', label: 'Hosting Premium', amount: 119, cost: 22 },
  ],
  GODEAL: [
    { code: 'GODEAL_BASE',   label: 'GoDeal Base',   amount: 149, cost: 97 },
    { code: 'GODEAL_PRO',    label: 'GoDeal Pro',    amount: 297, cost: 197 },
    { code: 'GODEAL_AGENCY', label: 'GoDeal Agency', amount: 597, cost: 297 },
  ],
  // Aura OS non ha un listino: ogni canone e' contrattato con lo studio e nasce
  // su Stripe, quindi qui non ci sono preset da proporre.
  AURA_OS: [],
  CUSTOM: [],
};

const DEFAULT_PROVIDER: Record<keyof typeof ManagedServiceType, 'STRIPE' | 'HIGHLEVEL' | 'MANUAL'> = {
  HOSTING: 'STRIPE',
  GODEAL: 'HIGHLEVEL',
  AURA_OS: 'STRIPE',
  CUSTOM: 'MANUAL',
};

export function NewServiceModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<keyof typeof ManagedServiceType>('HOSTING');
  const [clientId, setClientId] = useState('');
  const [planCode, setPlanCode] = useState('HOSTING_S');
  const [planLabel, setPlanLabel] = useState('Hosting Start');
  const [amount, setAmount] = useState(29);
  const [costAmount, setCostAmount] = useState(6);
  const [provider, setProvider] = useState<'STRIPE' | 'HIGHLEVEL' | 'MANUAL'>('STRIPE');
  const [status, setStatus] = useState<keyof typeof ServiceStatus>('TRIALING');
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY');
  const [externalId, setExternalId] = useState('');
  const [notes, setNotes] = useState('');

  const { data: clients } = useQuery({
    queryKey: ['clients-lite'],
    queryFn: () => api.get<{ items: ClientLite[] }>('/clients?pageSize=200'),
  });

  const applyPreset = (newType: keyof typeof ManagedServiceType, code?: string) => {
    setType(newType);
    setProvider(DEFAULT_PROVIDER[newType]);
    const presets = PLAN_PRESETS[newType];
    if (presets.length > 0) {
      const preset = code ? presets.find((p) => p.code === code) ?? presets[0] : presets[0];
      if (preset) {
        setPlanCode(preset.code);
        setPlanLabel(preset.label);
        setAmount(preset.amount);
        setCostAmount(preset.cost);
      }
    }
  };

  const create = useMutation({
    mutationFn: () => api.post('/services', {
      clientId, type, provider, status, billingCycle,
      planCode, planLabel, amount, costAmount,
      currency: 'EUR',
      externalId: externalId || null,
      notes: notes || null,
      startedAt: status === 'ACTIVE' ? new Date().toISOString() : null,
    }),
    onSuccess: () => {
      toast.ok('Servizio creato', `${planLabel} per cliente`);
      qc.invalidateQueries({ queryKey: ['services'] });
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const submit = () => {
    if (!clientId) { toast.warn('Seleziona un cliente'); return; }
    if (!planLabel.trim()) { toast.warn('Inserisci il nome del piano'); return; }
    if (amount < 0) { toast.warn('Importo non valido'); return; }
    create.mutate();
  };

  const presets = PLAN_PRESETS[type];

  return (
    <Modal
      title="Nuovo servizio gestito"
      kicker="Subscription · Hosting · GoDeal"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={submit} disabled={create.isPending}>
            Crea servizio <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Tipo</div>
        <div className="seg-control">
          {(Object.keys(ManagedServiceType) as Array<keyof typeof ManagedServiceType>).map((t) => (
            <button key={t} aria-pressed={type === t} onClick={() => applyPreset(t)}>
              {MANAGED_SERVICE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Cliente & piano</div>
        <div className="form-grid">
          <Field label="Cliente" required span={12}>
            <select className="inp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— seleziona —</option>
              {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.legalName}</option>)}
            </select>
          </Field>

          {presets.length > 0 && (
            <Field label="Preset piano" span={12}>
              <div className="chip-picker">
                {presets.map((p) => (
                  <span
                    key={p.code}
                    className={`chip ${planCode === p.code ? 'active' : 'ghost'} cursor-pointer`}
                    onClick={() => applyPreset(type, p.code)}
                  >
                    {p.label} <span className="mute mono">€ {p.amount}</span>
                  </span>
                ))}
              </div>
            </Field>
          )}

          <Field label="Codice piano" required span={4}>
            <input className="inp mono" value={planCode} onChange={(e) => setPlanCode(e.target.value)} />
          </Field>
          <Field label="Nome piano" required span={8}>
            <input className="inp" value={planLabel} onChange={(e) => setPlanLabel(e.target.value)} />
          </Field>

          <Field label="Importo ricavo" required span={4} hint="canone per ciclo (EUR)">
            <input className="inp mono" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
          </Field>
          <Field label="Costo provider" span={4} hint="costo da rivendita (HighLevel ecc.)">
            <input className="inp mono" type="number" min="0" step="0.01" value={costAmount} onChange={(e) => setCostAmount(Number(e.target.value))} />
          </Field>
          <Field label="Ciclo billing" span={4}>
            <select className="inp" value={billingCycle} onChange={(e) => setBillingCycle(e.target.value as any)}>
              <option value="MONTHLY">Mensile</option>
              <option value="QUARTERLY">Trimestrale</option>
              <option value="YEARLY">Annuale</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Provider & stato</div>
        <div className="form-grid">
          <Field label="Provider" span={6}>
            <div className="seg-control">
              {(Object.keys(ServiceProvider) as Array<keyof typeof ServiceProvider>).map((p) => (
                <button key={p} aria-pressed={provider === p} onClick={() => setProvider(p)}>
                  {p === 'STRIPE' ? 'Stripe' : p === 'HIGHLEVEL' ? 'HighLevel' : 'Manuale'}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Stato iniziale" span={6}>
            <select className="inp" value={status} onChange={(e) => setStatus(e.target.value as any)}>
              <option value="TRIALING">In trial</option>
              <option value="ACTIVE">Attivo</option>
              <option value="PAUSED">In pausa</option>
            </select>
          </Field>
          <Field label="External ID" span={12} hint="ID sottoscrizione Stripe / location HighLevel (opzionale, viene poi sincronizzato via webhook)">
            <input className="inp mono" placeholder={provider === 'STRIPE' ? 'sub_…' : provider === 'HIGHLEVEL' ? 'loc_…' : ''} value={externalId} onChange={(e) => setExternalId(e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Note interne</div>
        <textarea className="inp" rows={3} placeholder="Riferimenti contrattuali, dettagli onboarding, ecc." value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  );
}
